// lib/motion/ticker.ts — DER zentrale rAF-Scheduler (K-INTEGRATION §3).
//
// EINE requestAnimationFrame-Loop für das gesamte Cockpit. Instrumente
// (Rangierbahnhof I5, Uhrwerk, Baum-Diorama, Count-ups) registrieren hier
// ihre Per-Frame-Callbacks statt eigene Loops zu starten — Masterplan §7
// erlaubt max. 2 rAF-Loops GESAMT, wer einen eigenen committet fällt durch
// die Abnahme.
//
// Pausiert automatisch bei `document.hidden` (Tab-Wechsel → Batterie/CPU)
// und bei `prefers-reduced-motion` (dann startet gar keine Loop → statisch).
// Idempotent: mehrfaches subscribe/unsubscribe ist sicher, die Loop läuft
// nur solange ≥1 Subscriber aktiv ist.

/** nowMs = high-res Timestamp (rAF), dtMs = Delta zum letzten Frame (≈16.7). */
export type TickFn = (nowMs: number, dtMs: number) => void;

const subscribers = new Set<TickFn>();
let rafId: number | null = null;
let lastTs = 0;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function frame(ts: number): void {
  const dt = lastTs > 0 ? ts - lastTs : 16.7;
  lastTs = ts;
  // Kopie iterieren: ein Callback darf sich während des Frames abmelden.
  for (const fn of [...subscribers]) {
    try {
      fn(ts, dt);
    } catch {
      // Ein defekter Subscriber darf die globale Loop nie killen.
    }
  }
  rafId = requestAnimationFrame(frame);
}

function start(): void {
  if (rafId !== null) return;
  if (typeof document !== "undefined" && document.hidden) return;
  if (prefersReducedMotion()) return;
  lastTs = 0;
  rafId = requestAnimationFrame(frame);
}

function stop(): void {
  if (rafId !== null) {
    cancelAnimationFrame(rafId);
    rafId = null;
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stop();
    else if (subscribers.size > 0) start();
  });
}

/**
 * Registriert einen Per-Frame-Callback am zentralen Ticker.
 * @returns Unsubscribe-Funktion (in useEffect-Cleanup aufrufen).
 */
export function subscribe(fn: TickFn): () => void {
  subscribers.add(fn);
  start();
  return () => {
    subscribers.delete(fn);
    if (subscribers.size === 0) stop();
  };
}

/** Für Tests/Debug: läuft die Loop gerade? */
export function isTicking(): boolean {
  return rafId !== null;
}
